// src/components/Skeletons.js
//
// Loading placeholders that copy the page they stand in for — the same
// cards, table columns, row heights and grids — so nothing jumps when the
// real content arrives. A generic list would look identical on every page;
// these each match one layout.
//
// Table headers are printed as real text (they never depend on data), and
// only the cells shimmer.
import React from "react";
import { Card, Skeleton } from "./UI";


/* ── Building blocks ─────────────────────────────────────── */

const TH = { padding: "12px 20px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".06em" };

// One shimmering cell. `kind` picks the shape of the real cell it replaces.
function SkCell({ kind, i }) {
  const v = (i * 17) % 22; // small deterministic variation so rows don't look stamped
  switch (kind) {
    case "person":  return <div style={{ display: "flex", alignItems: "center", gap: 10 }}><Skeleton w={32} h={32} r={99} /><Skeleton w={90 + v} h={13} /></div>;
    case "person-sm": return <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Skeleton w={26} h={26} r={99} /><Skeleton w={80 + v} h={12} /></div>;
    case "two":     return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}><Skeleton w={110 + v} h={11} /><Skeleton w={140 + v} h={11} /></div>;
    case "badge":   return <Skeleton w={64 + v} h={22} r={99} />;
    case "status":  return <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Skeleton w={14} h={14} r={99} /><Skeleton w={62} h={22} r={99} /></div>;
    case "date":    return <Skeleton w={54} h={11} />;
    case "long":    return <Skeleton w={160 + v * 2} h={11} />;
    case "actions": return <div style={{ display: "flex", gap: 6 }}><Skeleton w={62} h={28} r={8} /><Skeleton w={62} h={28} r={8} /></div>;
    case "icons":   return <div style={{ display: "flex", gap: 6 }}><Skeleton w={26} h={26} r={6} /><Skeleton w={26} h={26} r={6} /></div>;
    case "check":   return <Skeleton w={16} h={16} r={4} />;
    default:        return <Skeleton w={70 + v} h={12} />;
  }
}

// A real table shell (Card + header labels) with shimmering body cells.
//   headers: labels, in order   cols: a cell kind per header
//   selectable: adds the leading checkbox column the Clients/Leads tables have
export function SkTable({ headers, cols, rows = 6, selectable = false, pad = "14px 20px", headBg = "var(--surface-2)", bare = false }) {
  const table = (
    <div className="rb-table-wrap" role="status" aria-busy="true" aria-label="Loading">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: headBg, borderBottom: "1px solid var(--border)" }}>
            {selectable && <th style={{ padding: "12px 14px", width: 36 }}><Skeleton w={16} h={16} r={4} /></th>}
            {headers.map((h, i) => <th key={h || `c${i}`} style={TH}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r} style={{ borderBottom: r < rows - 1 ? "1px solid var(--border)" : "none" }}>
              {selectable && <td style={{ padding: "14px 14px" }}><Skeleton w={16} h={16} r={4} /></td>}
              {cols.map((kind, c) => <td key={c} style={{ padding: pad }}><SkCell kind={kind} i={r * 7 + c} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return bare ? table : <Card style={{ overflow: "hidden", padding: 0 }}>{table}</Card>;
}

// The row of small stat tiles used on a page's top edge.
export function SkStatTiles({ n = 4, minWidth = 130 }) {
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }} role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: n }).map((_, i) => (
        <Card key={i} style={{ padding: "14px 20px", minWidth, display: "flex", flexDirection: "column", gap: 8 }}>
          <Skeleton w={40} h={22} /><Skeleton w={62} h={10} />
        </Card>
      ))}
    </div>
  );
}

// Search box + filter dropdown row.
export function SkToolbar({ search = true, filters = 1, pills = 0 }) {
  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
      {pills > 0 && Array.from({ length: pills }).map((_, i) => <Skeleton key={i} w={i === 0 ? 46 : 74} h={30} r={99} />)}
      {search && <Skeleton w={320} h={38} r={10} style={{ maxWidth: "100%" }} />}
      {Array.from({ length: filters }).map((_, i) => <Skeleton key={i} w={190} h={38} r={10} />)}
    </div>
  );
}

/* ── Client pages ────────────────────────────────────────── */

export const SkClients = () => (
  <SkTable selectable headers={["Client", "Contact", "Service", "Added", "Action"]} cols={["person", "two", "badge", "date", "actions"]} rows={7} />
);

export const SkClientCampaigns = () => (
  <SkTable headers={["Client", "Contact", "Service", "Source", "Status", "Outcome", "Sent"]} cols={["person", "text", "badge", "text", "status", "badge", "date"]} rows={7} />
);

// A review card: round sentiment icon, name + badges, quote lines, date on the right.
export function SkReviewCards({ rows = 4, showBusiness = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <Card key={i} style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <div style={{ display: "flex", gap: 14, flex: 1, minWidth: 0 }}>
              <Skeleton w={42} h={42} r={99} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Skeleton w={110 + ((i * 19) % 30)} h={14} />
                  <Skeleton w={66} h={22} r={99} />
                  <Skeleton w={78} h={22} r={99} />
                </div>
                {showBusiness && <Skeleton w={200} h={11} />}
                <Skeleton w="88%" h={12} />
                <Skeleton w={`${45 + ((i * 23) % 35)}%`} h={12} />
              </div>
            </div>
            <Skeleton w={70} h={11} />
          </div>
        </Card>
      ))}
    </div>
  );
}

// Service areas: a state pill + divider, then a grid of small cards.
export function SkLocations() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }} role="status" aria-busy="true" aria-label="Loading">
      {[6, 3].map((count, g) => (
        <div key={g}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Skeleton w={140} h={26} r={99} />
            <div style={{ height: 1, flex: 1, background: "var(--border)" }} />
            <Skeleton w={54} h={10} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 10 }}>
            {Array.from({ length: count }).map((_, i) => (
              <Card key={i} style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
                <Skeleton w={`${55 + ((i * 11) % 30)}%`} h={14} />
                <Skeleton w="40%" h={11} />
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Overview's small stats row inside the website/Zilo cards.
export const SkInlineStats = ({ n = 3 }) => (
  <div style={{ display: "flex", gap: 12, paddingTop: 20, borderTop: "1px solid var(--border)" }} role="status" aria-busy="true" aria-label="Loading">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} style={{ flex: 1, padding: "12px 14px", borderRadius: 10, background: "var(--surface-2)", border: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 8 }}>
        <Skeleton w={30} h={18} /><Skeleton w={64} h={10} />
      </div>
    ))}
  </div>
);

// Invoices table on Billing.
export const SkInvoices = () => (
  <SkTable bare headers={["Date", "Description", "Amount", "Status", ""]} cols={["date", "long", "text", "badge", "icons"]} rows={3} />
);

// Full client dashboard: greeting, Zilo hero card, stat cards, checklist.
export function SkDashboard() {
  return (
    <div className="rb-page" style={{ padding: "36px 40px", maxWidth: 1100 }} role="status" aria-busy="true" aria-label="Loading">
      <div style={{ marginBottom: 32, display: "flex", flexDirection: "column", gap: 10 }}>
        <Skeleton w={130} h={14} /><Skeleton w={260} h={28} /><Skeleton w={300} h={13} />
      </div>
      <Card style={{ padding: "26px 30px", marginBottom: 24, display: "flex", alignItems: "center", gap: 20 }}>
        <Skeleton w={76} h={76} r={99} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}><Skeleton w={120} h={24} /><Skeleton w={84} h={24} r={99} /></div>
          <Skeleton w={220} h={13} /><Skeleton w="60%" h={11} />
        </div>
      </Card>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14, marginBottom: 24 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
            <Skeleton w={90} h={11} /><Skeleton w={64} h={28} /><Skeleton w={110} h={10} />
          </Card>
        ))}
      </div>
      <Card style={{ padding: "22px 26px", display: "flex", flexDirection: "column", gap: 14 }}>
        <Skeleton w={160} h={16} />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}><Skeleton w={20} h={20} r={99} /><Skeleton w={`${35 + i * 8}%`} h={12} /></div>
        ))}
      </Card>
    </div>
  );
}

/* ── Admin pages ─────────────────────────────────────────── */

// Task board rows use one shared grid so the columns line up with the header.
const TASK_COLS = "150px minmax(240px,1fr) 150px 190px 110px 120px 150px";
export function SkTaskRows({ rows = 8 }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: TASK_COLS, gap: 14, alignItems: "center", padding: "14px 20px 14px 17px", borderTop: "1px solid var(--border)", borderLeft: "3px solid transparent" }}>
          <Skeleton w={78} h={11} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}><Skeleton w={`${50 + ((i * 13) % 40)}%`} h={13} />{i % 3 === 0 && <Skeleton w={64} h={18} r={99} />}</div>
          <Skeleton w={90} h={11} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Skeleton w={26} h={26} r={99} /><Skeleton w={78} h={11} /></div>
          <Skeleton w={78} h={26} r={7} />
          <Skeleton w={70} h={22} r={6} />
          <Skeleton w={70} h={11} />
        </div>
      ))}
    </div>
  );
}

// Bookings list: a date banner then call rows.
export function SkBookings({ groups = 2, perGroup = 3 }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: groups }).map((_, g) => (
        <div key={g}>
          <div style={{ padding: "9px 20px", background: "var(--surface-2)", borderTop: "1px solid var(--border)" }}><Skeleton w={110} h={11} /></div>
          {Array.from({ length: perGroup }).map((_, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderTop: "1px solid var(--border)" }}>
              <Skeleton w={70} h={13} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}><Skeleton w={`${30 + ((i * 17) % 25)}%`} h={13} /><Skeleton w="45%" h={11} /></div>
              <Skeleton w={90} h={22} r={99} />
              <Skeleton w={28} h={28} r={8} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Business cards on the admin Businesses page.
export function SkBusinessCards({ rows = 3 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <Card key={i} style={{ padding: "22px 26px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <Skeleton w={48} h={48} r={12} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}><Skeleton w={150 + ((i * 20) % 50)} h={16} /><Skeleton w={58} h={22} r={99} /></div>
              <Skeleton w={280} h={11} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 14 }}><Skeleton w={130} h={22} r={99} /><Skeleton w={110} h={22} r={99} /></div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
            {Array.from({ length: 4 }).map((_, k) => <Skeleton key={k} w={96} h={32} r={8} />)}
          </div>
        </Card>
      ))}
    </div>
  );
}

// Admin campaigns are grouped by business: a header bar, then a table.
export function SkCampaignGroups({ groups = 3 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }} role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: groups }).map((_, g) => (
        <Card key={g} style={{ overflow: "hidden", padding: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", background: "var(--surface-2)", borderBottom: "1px solid var(--border)" }}>
            <Skeleton w={15} h={15} r={4} /><Skeleton w={150 + g * 20} h={15} />
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}><Skeleton w={64} h={22} r={99} /><Skeleton w={78} h={22} r={99} /></span>
          </div>
          <SkTable bare headers={["Client", "Contact", "Status", "Sent", "Actions"]} cols={["person", "text", "status", "date", "icons"]} rows={g === 0 ? 3 : 2} headBg="var(--surface)" />
        </Card>
      ))}
    </div>
  );
}

export const SkLeads = () => (
  <SkTable selectable headers={["Lead", "Phone", "Email", "Industry", "City", "Status", "Notes", "Date Added", "Last Updated", ""]}
    cols={["person", "text", "text", "text", "text", "badge", "long", "date", "date", "icons"]} rows={8} />
);

export const SkTeam = () => (
  <SkTable headers={["Name", "Email", "Department", "Access", ""]} cols={["person-sm", "long", "text", "badge", "icons"]} rows={5} pad="12px 20px" />
);

export const SkAuditLog = () => (
  <SkTable headers={["When", "Admin", "Action", "Target", "Details"]} cols={["date", "text", "badge", "text", "long"]} rows={8} pad="12px 20px" />
);

// Website projects: filter pills, then project cards with a progress bar.
export function SkProjects({ rows = 4 }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading">
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {[54, 76, 100, 70, 84, 96, 60, 70].map((w, i) => <Skeleton key={i} w={w} h={32} r={99} />)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {Array.from({ length: rows }).map((_, i) => (
          <Card key={i} style={{ padding: "20px 24px", display: "flex", alignItems: "center", gap: 16 }}>
            <Skeleton w={44} h={44} r={12} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 9 }}>
              <Skeleton w={`${25 + ((i * 11) % 20)}%`} h={15} />
              <div style={{ display: "flex", gap: 3 }}>{Array.from({ length: 7 }).map((_, k) => <Skeleton key={k} w={14} h={4} r={99} />)}</div>
            </div>
            <Skeleton w={90} h={24} r={99} /><Skeleton w={90} h={32} r={8} />
          </Card>
        ))}
      </div>
    </div>
  );
}

// A generic form-style card stack (blogs settings, website builder, widgets).
export function SkCards({ rows = 3 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }} role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <Card key={i} style={{ padding: "22px 26px", display: "flex", flexDirection: "column", gap: 14 }}>
          <Skeleton w={190} h={16} />
          <Skeleton w="100%" h={38} r={10} />
          <Skeleton w="100%" h={38} r={10} />
          <Skeleton w={110} h={34} r={8} />
        </Card>
      ))}
    </div>
  );
}

// Admin overview: three money cards, customers card with four tiles, a list card.
export function SkAdminOverview() {
  return (
    <div role="status" aria-busy="true" aria-label="Loading">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14, marginBottom: 16 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <Skeleton w={110} h={11} /><Skeleton w={130} h={30} /><Skeleton w={150} h={11} />
          </Card>
        ))}
      </div>
      <Card style={{ padding: "22px 26px", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}><Skeleton w={100} h={12} /><Skeleton w={190} h={11} /></div>
          <Skeleton w={80} h={12} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ padding: "16px 18px", borderRadius: 12, background: "var(--surface-2)", border: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 10 }}>
              <Skeleton w={70} h={11} /><Skeleton w={44} h={28} /><Skeleton w="100%" h={3} r={99} />
            </div>
          ))}
        </div>
      </Card>
      <Card style={{ padding: "22px 26px", display: "flex", flexDirection: "column", gap: 14 }}>
        <Skeleton w={150} h={14} />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}><Skeleton w={8} h={8} r={99} /><Skeleton w={`${40 + ((i * 13) % 35)}%`} h={12} /><Skeleton w={70} h={20} r={99} style={{ marginLeft: "auto" }} /></div>
        ))}
      </Card>
    </div>
  );
}

// Time tracker "My time": page heading, big clock card, stat tiles, entries table.
export function SkTimeTracker() {
  return (
    <div className="rb-page" style={{ padding: "32px 36px", maxWidth: 1040 }} role="status" aria-busy="true" aria-label="Loading">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22, gap: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}><Skeleton w={170} h={26} /><Skeleton w={330} h={13} /></div>
        <Skeleton w={260} h={34} r={9} />
      </div>
      <Card style={{ padding: "24px 28px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}><Skeleton w={150} h={34} /><Skeleton w={200} h={12} /></div>
        <Skeleton w={130} h={42} r={10} />
      </Card>
      <SkStatTiles n={3} minWidth={160} />
      <SkTable headers={["Date", "Clock in", "Clock out", "Duration", "Earned"]} cols={["date", "text", "text", "text", "text"]} rows={5} />
    </div>
  );
}

// Pay rates: one row per person with rate chips and an edit button.
export function SkPayRates({ rows = 4 }) {
  return (
    <Card style={{ padding: 0, overflow: "hidden" }} role="status" aria-busy="true" aria-label="Loading">
      <div style={{ padding: "16px 22px", borderBottom: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 8 }}><Skeleton w={100} h={14} /><Skeleton w="70%" h={11} /></div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: "14px 22px", borderTop: i ? "1px solid var(--border)" : "none" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Skeleton w={120 + ((i * 17) % 40)} h={13} />
            <div style={{ display: "flex", gap: 6 }}><Skeleton w={110} h={20} r={99} /><Skeleton w={96} h={20} r={99} /></div>
          </div>
          <Skeleton w={84} h={30} r={8} />
        </div>
      ))}
    </Card>
  );
}

// Review widgets: heading + a list of widget cards.
export function SkWidgets({ rows = 4 }) {
  return (
    <div className="rb-page" style={{ padding: "32px 36px", maxWidth: 1180 }} role="status" aria-busy="true" aria-label="Loading">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22, gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}><Skeleton w={190} h={26} /><Skeleton w={340} h={13} /></div>
        <Skeleton w={130} h={38} r={10} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px,1fr))", gap: 16 }}>
        {Array.from({ length: rows }).map((_, i) => (
          <Card key={i} style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
            <Skeleton w={32} h={32} r={8} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}><Skeleton w="70%" h={13} /><Skeleton w="50%" h={11} /></div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Costs: two provider cards side by side (icon + big number + rows).
export function SkCostCards() {
  return (
    <div className="rb-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }} role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: 2 }).map((_, i) => (
        <Card key={i} style={{ padding: "22px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <Skeleton w={36} h={36} r={10} />
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}><Skeleton w={110} h={14} /><Skeleton w={100} h={20} r={99} /></div>
          </div>
          <Skeleton w={150} h={32} style={{ marginBottom: 8 }} />
          <Skeleton w={110} h={11} style={{ marginBottom: 18 }} />
          {[0, 1, 2].map(k => <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}><Skeleton w={110} h={12} /><Skeleton w={60} h={12} /></div>)}
        </Card>
      ))}
    </div>
  );
}

/* ── Route-level fallback ────────────────────────────────── */
// While a page's code is still downloading its own component doesn't exist
// yet, so the WHOLE page (heading included) is drawn here. The first path
// segment picks which layout.
const Head = ({ actions = 0, subtitle = true }) => (
  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16, flexWrap: "wrap" }}>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}><Skeleton w={200} h={28} />{subtitle && <Skeleton w={360} h={13} />}</div>
    {actions > 0 && <div style={{ display: "flex", gap: 8 }}>{Array.from({ length: actions }).map((_, i) => <Skeleton key={i} w={104} h={38} r={10} />)}</div>}
  </div>
);
const Page = ({ children, max = 1100 }) => <div className="rb-page" style={{ padding: "36px 40px", maxWidth: max }}>{children}</div>;

export function RoutePageSkeleton({ pathname = "" }) {
  const p = pathname.replace(/\/+$/, "");
  const seg = p.split("/").filter(Boolean);
  const isAdmin = seg[0] === "admin";
  const key = isAdmin ? (seg[1] || "overview") : (seg[0] || "dashboard");

  if (!isAdmin && (key === "dashboard" || key === "overview")) return <SkDashboard />;
  if (isAdmin && key === "overview") return <Page><Head /><SkAdminOverview /></Page>;

  const layouts = {
    // client
    clients:   () => <Page><Head actions={2} /><SkToolbar filters={0} /><SkClients /></Page>,
    campaigns: () => isAdmin
      ? <Page><Head /><SkToolbar /><SkCampaignGroups /></Page>
      : <Page><Head /><SkClientCampaigns /></Page>,
    reviews:   () => <Page max={1000}><Head />{isAdmin && <SkStatTiles />}<SkToolbar filters={isAdmin ? 1 : 0} pills={3} /><SkReviewCards showBusiness={isAdmin} /></Page>,
    locations: () => <Page><Head actions={1} /><SkToolbar filters={0} /><SkLocations /></Page>,
    billing:   () => <Page max={900}><Head /><SkCards rows={2} /></Page>,
    // admin
    tasks:      () => <Page max={1200}><Head actions={1} /><SkToolbar filters={2} /><Card style={{ padding: 0, overflow: "hidden" }}><SkTaskRows /></Card></Page>,
    bookings:   () => <Page max={1000}><Head /><Card style={{ padding: 0, overflow: "hidden" }}><SkBookings /></Card></Page>,
    businesses: () => <Page><Head actions={1} /><SkToolbar /><SkBusinessCards /></Page>,
    leads:      () => <Page max={1300}><Head actions={2} /><SkToolbar filters={2} /><SkLeads /></Page>,
    team:       () => <Page><Head /><SkTeam /></Page>,
    "audit-log": () => <Page><Head /><SkAuditLog /></Page>,
    website:    () => <Page><Head /><SkProjects /></Page>,
    time:       () => <SkTimeTracker />,
    "review-widgets": () => <SkWidgets />,
    costs:      () => <Page><Head actions={1} /><SkCostCards /><SkTable headers={["Business", "SMS", "Email", "Share", "Est. cost"]} cols={["person-sm", "text", "text", "text", "text"]} rows={5} /></Page>,
  };
  const render = layouts[key];
  if (render) return render();
  return <Page><Head /><SkCards rows={3} /></Page>;
}
